import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { PayloadStates } from '@lore/utils';
import { useConnect } from '@lore/query-connect';
import useProjectChannel from '@/hooks/useProjectChannel';
import Loader from '../../components/Loader';
import ActionBar from './ActionBar';
import Project from '../../pages/project/Project';
import ProjectEditLayout from '../../pages/project-edit/Dialog';
import ProjectManageLayout from '../../pages/project-manage/Dialog';
import ProjectClassifyLayout from '../../pages/project-classify/Dialog';
import ProjectInvitePopulateLayout from '../../pages/project-invite-populate/Dialog';
import ProjectInviteLayout from '../../pages/project-invite/Dialog';
import useDialogConfig from '../_hooks/useDialogConfig';
import useDialogRouter from '../_hooks/useDialogRouter';
import MissingFields from '../../pages/project-manage/MissingFields';
import Banner from './Banner';

export default function ProjectManageDialogLayout(props) {
  // Subscribe to the live record from the store (keyed by id) rather than
  // rendering off the `project` payload passed in as a prop. That prop can be a
  // stale snapshot held by an ancestor that doesn't re-render when the byId
  // cache is updated (e.g., after a visibility change). An
  // ancestor has already fetched this project, so this resolves from cache with
  // no extra request while making the whole dialog subtree reactive to byId
  // cache writes.
  const project = useConnect('project.byId', {
    id: props.project.id
  });

  const { id, route } = useDialogConfig();
  const router = useDialogRouter(id);

  useEffect(() => {
    if (route === '/') {
      router.navigate('/manage');
    }
  }, []);

  // Notify an open public preview tab (e.g. /share/project/:id) that this
  // project changed, so it can re-pull the public projection. Because this
  // layout holds the live byId record, it covers the full set of edits made
  // anywhere in the manage flow — not just the title/image that individual
  // sub-dialogs broadcast.
  //
  // We broadcast on every resolved render rather than trying to diff the data:
  // the stacked sub-dialogs cause this layout to re-mount around save
  // boundaries, which resets any "last snapshot" ref and makes change detection
  // unreliable. The extra broadcasts are harmless — refresh() on the receiver
  // is an idempotent, cache-busted refetch that no-ops (via react-query
  // structural sharing) when nothing actually changed. The RESOLVED guard keeps
  // us off the optimistic UPDATING frame, so the preview never refetches before
  // the server has the change.
  const broadcast = useProjectChannel(project.id);
  useEffect(() => {
    if (project.state === PayloadStates.RESOLVED) {
      broadcast();
    }
  }, [project.state, project.data, broadcast]);

  if (project.state === PayloadStates.FETCHING) {
    return (
      <Loader />
    );
  }

  const pageMap = {
    '/manage': 'Manage',
    '/view': 'View',
    '/edit': 'Edit',
    '/invite-populate': 'Invite',
    '/invite': 'Invite',
    '/classify': 'Classify'
  };

  const pathname = route.split('?')[0];

  // const page = pageMap[route] || route;
  const page = pageMap[pathname] || pathname;

  return (
    <div className="space-y-6">
      <Helmet>
        <title>
          {project.data.title ? `${project.data.title}: ${page}` : `Project #${project.id}: ${page}`}
        </title>
      </Helmet>
      <Banner project={project} />
      {/*<div className="-mx-6">*/}
        <ActionBar project={project} />
      {/*</div>*/}
      <div className="space-y-12">
        {pathname === `/view` && (
          <Project project={project} />
        )}
        {pathname === `/edit` && (
          <>
            <MissingFields project={project} />
            <ProjectEditLayout
              project={project}
              onClose={props.onCancel}
            />
          </>
        )}
        {pathname === `/invite-populate` && (
          <ProjectInvitePopulateLayout
            project={project}
            onClose={props.onCancel}
          />
        )}
        {pathname === `/invite` && (
          <ProjectInviteLayout
            project={project}
            router={router}
            onClose={props.onCancel}
          />
        )}
        {pathname === `/manage` && (
          <ProjectManageLayout
            project={project}
            onClose={props.onCancel}
          />
        )}
        {pathname === `/classify` && (
          <ProjectClassifyLayout
            project={project}
            onClose={props.onCancel}
          />
        )}
      </div>
    </div>
  );
};

ProjectManageDialogLayout.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]).isRequired
  }).isRequired,
  onCancel: PropTypes.func
};
